/**
 * What a screen reader should hear from the door panel right now.
 *
 * Split out of the component so it can be tested directly — the bug it fixes is invisible in
 * markup and only shows up in what a screen reader actually says.
 *
 * @module domains/access/DoorAccessPanel/panelAnnouncement
 */

export interface PanelAnnouncementInput {
  error?: string
  status?: string
  title: string
  subtitle?: string
  /** The panel is a terminal confirmation — the whole column is the message. */
  announce?: boolean
}

/**
 * Most urgent first, with one exception.
 *
 * Normally the subtitle is the live line and the title is static chrome, so announcing the
 * subtitle alone is right. But on a terminal confirmation — `announce` — the title IS the
 * outcome: "Door Unlocked", with the subtitle carrying the detail. Announcing only the
 * subtitle there tells a screen-reader user which doors opened without ever saying that
 * anything opened.
 *
 * `error` and `status` still win when present. The obvious formula —
 * `announce ? title + subtitle : (error ?? status ?? subtitle)` — drops an error whenever
 * `announce` is set, which is exactly backwards for the one message that must interrupt.
 */
export function panelAnnouncement({
  error,
  status,
  title,
  subtitle,
  announce = false,
}: PanelAnnouncementInput): string | undefined {
  if (error) return error
  if (status) return status
  if (announce) return [title, subtitle].filter(Boolean).join('. ')
  return subtitle
}
